// ---------------------------------------------------------------------------
// Narrowed application types.
//
// Generated Supabase types render CHECK-constrained text columns as `string`,
// because the constraint is not a Postgres enum. These aliases re-apply the
// domain so the compiler catches an outcome the UI does not handle.
// ---------------------------------------------------------------------------

import type {
  AddressSource,
  BestReturnTime,
  Gender,
  InaccessibleReason,
  Literature,
  Party,
  Role,
  SupportLevel,
  VisitOutcome,
  VoterTier,
} from './enums.js';
import type { Tables } from './database.types.js';

/**
 * `location` is deliberately absent. It is a generated PostGIS column that
 * serialises badly through PostgREST, so the client reads lat/lon and every
 * spatial operation happens server-side (PLAN 4.2). Queries must name their
 * columns rather than `select('*')` to keep this true.
 */
export type Household = Omit<Tables<'households'>, 'source' | 'location' | 'house_notes'> & {
  source: AddressSource;
};

/**
 * A note about an address, kept across visits.
 *
 * Append-only. Two canvassers writing about the same house produce two notes;
 * neither overwrites the other (20260908000300). Hand-declared until
 * `npm run types:gen:remote` is re-run.
 */
export interface HouseholdNote {
  id: string;
  household_id: string;
  author_id: string;
  body: string;
  created_at: string;
  received_at: string | null;
}

/** The derived-status view, for organizer coverage reporting. */
export type HouseholdStatus = Tables<'household_status'>;

/**
 * The door event. Support moved to PersonContact when the campaign adopted
 * person-level data (docs/campaign-requirements.md § 3.1), so `support` is
 * omitted here and not replaced.
 */
export type Visit = Omit<
  Tables<'visits'>,
  'outcome' | 'support' | 'literature' | 'best_return_time'
> & {
  outcome: VisitOutcome;
  inaccessible_reason: InaccessibleReason | null;
  literature: Literature;
  best_return_time: BestReturnTime | null;
};

/**
 * A registered voter in the walk universe.
 *
 * Hand-declared rather than derived from `Tables<'people'>` because
 * database.types.ts is generated from the live schema and must never be
 * hand-edited (docs/deviations-from-plan.md). Once 20260907000100 is applied
 * and `npm run types:gen:remote` has run, replace this with a Tables<> alias.
 *
 * This is California voter registration information. See
 * docs/campaign-requirements.md § 5 before putting it anywhere new.
 */
export interface Person {
  id: string;
  household_id: string;
  external_id: string;
  first_name: string;
  middle_name: string | null;
  last_name: string;
  suffix: string | null;
  party: Party | null;
  gender: Gender | null;
  age: number | null;
  age_as_of: string | null;
  registration_status: 'active' | 'inactive' | null;
  vbm_status: string | null;
  precinct: string | null;
  tier: VoterTier | null;
  turnout_score: number | null;
  cell_phone: string | null;
  landline: string | null;
  do_not_call: boolean;
  callable: boolean;
  mailable: boolean;
  deceased: boolean;
  moved: boolean;
  active: boolean;
  created_at: string;
  updated_at: string;
}

/** Also hand-declared until the migration is applied. Append-only. */
export interface PersonContact {
  id: string;
  visit_id: string;
  person_id: string;
  canvasser_id: string;
  contacted_at: string;
  received_at: string | null;
  spoke: boolean;
  support: SupportLevel | null;
  sign_requested: boolean;
  notes: string | null;
}

/** A person plus their most recent contact, for the household detail list. */
export type PersonWithContact = Person & {
  last_contact: PersonContact | null;
  contact_count: number;
};

/** Full name, with the parts the file leaves blank omitted rather than spaced. */
export function formatName(p: {
  first_name: string;
  last_name: string;
  suffix?: string | null;
}): string {
  return [p.first_name, p.last_name, p.suffix].filter(Boolean).join(' ');
}

/**
 * Attach the latest contact to each person. Same shape as withStatus, and for
 * the same reason: contacts are append-only, so "current" is a fold over
 * history rather than a column.
 */
export function withContacts(
  people: readonly Person[],
  contacts: readonly PersonContact[],
): PersonWithContact[] {
  const byPerson = new Map<string, PersonContact[]>();
  for (const c of contacts) {
    const list = byPerson.get(c.person_id);
    if (list) list.push(c);
    else byPerson.set(c.person_id, [c]);
  }
  return people.map((p) => {
    const list = byPerson.get(p.id) ?? [];
    let latest: PersonContact | null = null;
    for (const c of list) {
      if (!latest || c.contacted_at > latest.contacted_at) latest = c;
    }
    return { ...p, last_contact: latest, contact_count: list.length };
  });
}

export type Profile = Omit<Tables<'profiles'>, 'role'> & { role: Role };

export type Turf = Tables<'turfs'>;
export type TurfAssignment = Tables<'turf_assignments'>;

/** A household plus its most recent visit, derived client-side. */
export type HouseholdWithStatus = Household & {
  last_visit: Visit | null;
  visit_count: number;
};

/**
 * Map pin / list row buckets. Deliberately coarser than VisitOutcome -- a
 * canvasser standing on a sidewalk needs to know "knock this one or not",
 * not the full taxonomy.
 */
export type CoverageState =
  | 'unvisited'
  | 'not_home'
  | 'contacted'
  | 'skip'
  | 'do_not_contact';

export function coverageState(h: HouseholdWithStatus): CoverageState {
  if (h.do_not_contact) return 'do_not_contact';
  if (!h.last_visit) return 'unvisited';
  switch (h.last_visit.outcome) {
    case 'not_home':
      return 'not_home';
    case 'answered':
      return 'contacted';
    case 'inaccessible':
    case 'vacant':
    case 'wrong_address':
      return 'skip';
  }
}

/**
 * How long a canvasser may take back a do-not-contact flag they set themselves.
 *
 * Mirrors the `undo_window` constant in clear_do_not_contact()
 * (supabase/migrations/20260908001100_undo_do_not_contact.sql) -- change both
 * together. The server is the authority; this only decides whether the visit
 * sheet draws the button, and a device with a drifting clock can offer an undo
 * the server then refuses.
 */
export const DO_NOT_CONTACT_UNDO_MINUTES = 30;

/**
 * Whether this viewer may clear the flag from the visit sheet.
 *
 * Organizers always may: that is the standing rule (PLAN 11), and the reports
 * screen has offered it since 20260904000100. A canvasser may undo only their
 * own flag and only while it is new, because the case this exists for is the
 * mis-tap noticed on the same doorstep -- `Flag do not contact` is one tap with
 * no confirm. An older flag is somebody's recorded request not to be contacted,
 * and taking it back is not the canvasser's call.
 *
 * A flag with no `do_not_contact_at` predates that column and is organizers-only,
 * which is the rule it was set under.
 */
export function canUndoDoNotContact(
  h: Pick<Household, 'do_not_contact' | 'do_not_contact_at' | 'do_not_contact_by'>,
  viewer: { id: string; role: Role },
  now: Date = new Date(),
): boolean {
  if (!h.do_not_contact) return false;
  if (viewer.role === 'organizer' || viewer.role === 'admin') return true;
  if (h.do_not_contact_by === null || h.do_not_contact_by !== viewer.id) return false;
  if (h.do_not_contact_at === null) return false;

  const setAt = Date.parse(h.do_not_contact_at);
  if (Number.isNaN(setAt)) return false;
  return now.getTime() - setAt < DO_NOT_CONTACT_UNDO_MINUTES * 60_000;
}

export function formatAddress(h: {
  street_address: string;
  unit: string | null;
}): string {
  return h.unit ? `${h.street_address} #${h.unit}` : h.street_address;
}

/**
 * How far through a turf the campaign is, 0-100.
 *
 * A turf with no households is 0 rather than NaN, and reads on the organize
 * map as untouched -- which is what an empty turf is. Rounded here, once, so
 * the map's shading, its label and the sidebar can never disagree about a
 * number the organizer is comparing across three places on one screen.
 */
export function knockedPct(turf: {
  household_count: number;
  knocked_count: number;
}): number {
  if (turf.household_count <= 0) return 0;
  return Math.round((turf.knocked_count / turf.household_count) * 100);
}

/** Attach the latest visit to each household. Visits are append-only. */
export function withStatus(
  households: readonly Household[],
  visits: readonly Visit[],
): HouseholdWithStatus[] {
  const byHousehold = new Map<string, Visit[]>();
  for (const v of visits) {
    const list = byHousehold.get(v.household_id);
    if (list) list.push(v);
    else byHousehold.set(v.household_id, [v]);
  }
  return households.map((h) => {
    const list = byHousehold.get(h.id) ?? [];
    let latest: Visit | null = null;
    for (const v of list) {
      if (!latest || v.visited_at > latest.visited_at) latest = v;
    }
    return { ...h, last_visit: latest, visit_count: list.length };
  });
}

// --- invite codes (20260910000100_invites.sql) ------------------------------
//
// Nobody types or reads one any more: since 20260927000100 create_account()
// mints and redeems a code in one statement, and the scripts do the same via
// scripts/lib/invite.ts. What is left here is what that script needs.

/**
 * No I, O, 0 or 1. A code gets read down a phone line to somebody standing in
 * a car park, and those four are where that goes wrong. 32 symbols, so the
 * modulo the generator uses is uniform over a byte.
 */
export const INVITE_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
export const INVITE_CODE_LENGTH = 12;

/**
 * Reduces whatever was typed to the twelve characters that are actually the
 * code, so `abcd efgh jkmn`, `ABCD-EFGH-JKMN` and a pasted link fragment all
 * compare equal.
 *
 * MIRRORS invite_code_digest() in the migration -- change both together. Note
 * that anything outside the alphabet is dropped rather than rejected: that is
 * what removes the grouping dashes, and a stray O or 0 in a hand-copied code
 * is noise, since neither can occur in a real one.
 */
export function normalizeInviteCode(input: string): string {
  return input.toUpperCase().replace(/[^ABCDEFGHJKLMNPQRSTUVWXYZ23456789]/g, '');
}
